import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";

import { GridInputs } from "../../../../../../../components/Functional";
import { Button } from "../../../../../../../components/ui";

import { CREATE_ITEM_CREDIT as createItemCreditMutation } from "../../../../../../../graphql/mutation/Item";
import { FETCH_NOMINALS } from "../../../../../../../graphql/query/Nominal";
import { FETCH_ITEM_CREDIT_DETAILS as fetchItemCreditDetailsQuery } from "../../../../../../../graphql/query/Item";
import {
  buildCreditAllRows,
  buildCreditAllocations,
  buildCreditRow,
  getDefaultCreditAllocation,
  getDefaultCreditReferenceId,
  getCreditAllSourceItems,
  getNextCreditRowNumber,
} from "./creditNoteAllocation";
import { buildCreditNoteValidationSchema } from "./creditNoteValidation";

const toCurrencyCents = value =>
  Math.round((Number(value) + Number.EPSILON) * 100);

const AddCreditNoteBody = props => {
  const { invoice, onTypeChange, resetAll, typeOptions = [] } = props || {};

  const [fetchItemCreditDetails] = useLazyQuery(fetchItemCreditDetailsQuery, {
    fetchPolicy: "network-only",
  });

  const [
    createItemCredit,
    {
      data: {
        createItemCredit: { error: createItemCreditRespError } = {},
      } = {},
      error: createItemCreditError,
    },
  ] = useMutation(createItemCreditMutation(), {
    refetchQueries: ["FetchItemsV2", "FetchInvoice"],
  });

  const { data: { nominals = [] } = {} } = useQuery(FETCH_NOMINALS);
  const [creditAllItemLoading, setCreditAllItemLoading] = useState(false);
  const [creditAllError, setCreditAllError] = useState("");
  const prefillingCreditAllRef = useRef(false);

  const creditFormik = useFormik({
    initialValues: {
      invoiceId: invoice?.id,
      items: [],
      typeId: 6, // typeId 6 is for CreditNote
    },
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: () =>
      buildCreditNoteValidationSchema(creditFormik?.values),
    onSubmit: async values => {
      creditFormik?.setFieldValue("creditItemLoading", true);
      const items = buildCreditAllocations(values?.items || [], values);
      const amount =
        items.reduce(
          (total, item) => total + toCurrencyCents(item?.amount || 0),
          0,
        ) / 100;

      const finalValues = {
        amount,
        invoiceId: values?.invoiceId,
        typeId: values?.typeId,
        items,
      };

      createItemCredit({
        variables: {
          input: finalValues,
        },
      })
        .then(resp => {
          const { data: { createItemCredit: { success } = {} } = {} } =
            resp || {};
          if (success) {
            resetAll();
            creditFormik?.resetForm?.();
          } else {
            creditFormik?.setFieldValue("creditItemLoading", false);
          }
        })
        .catch(() => {
          creditFormik?.setFieldValue("creditItemLoading", false);
        });
    },
  });

  const { creditItemLoading } = creditFormik?.values || {};
  const creditValues = creditFormik.values;
  const setCreditFieldValue = creditFormik.setFieldValue;

  const prevFormValuesRef = useRef(creditValues);
  useEffect(() => {
    const changedItemIDs = Object.keys(creditValues)
      .filter(
        key =>
          /^id_\d+$/.test(key) &&
          creditValues[key] !== prevFormValuesRef.current[key],
      )
      .map(key => parseInt(key.replace("id_", ""), 10));

    if (prefillingCreditAllRef.current) {
      prefillingCreditAllRef.current = false;
    } else if (creditValues[`id_${changedItemIDs[0]}`]) {
      fetchItemCreditDetails({
        variables: {
          id: creditValues[`id_${changedItemIDs[0]}`],
        },
      }).then(resp => {
        const {
          data: {
            itemCreditDetails: {
              amount,
              description,
              nominalId,
              remainingCreditAmount,
            } = {},
          },
        } = resp || {};
        const availableAmount = remainingCreditAmount ?? amount;
        const currentAmountField = `amount_${changedItemIDs[0]}`;
        const defaultAllocation = getDefaultCreditAllocation({
          availableAmount,
        });
        setCreditFieldValue(`nominalId_${changedItemIDs[0]}`, nominalId);
        setCreditFieldValue(`description_${changedItemIDs[0]}`, description);
        setCreditFieldValue(
          `remainingAmount_${changedItemIDs[0]}`,
          availableAmount,
        );
        setCreditFieldValue(currentAmountField, defaultAllocation);
      });
    }

    prevFormValuesRef.current = creditValues;
  }, [creditValues, fetchItemCreditDetails, setCreditFieldValue]);

  const hasCreditErrors = Object.keys(creditFormik?.errors || {}).length > 0;

  const mutationError = createItemCreditError || createItemCreditRespError;
  const formError = mutationError || creditAllError;

  const creditInputs = {
    className: "mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-8",
    inputs: [
      {
        label: "Type",
        name: "typeId",
        onChange: onTypeChange,
        options: typeOptions,
        type: "multi-select",
      },
    ],
  };

  const creditInputProps = {
    formik: creditFormik,
    ...creditInputs,
  };

  const inputConfigs = {
    nominalId: {
      type: "multi-select",
      options: nominals?.map(item => ({ label: item?.label, value: item?.id })),
      appendMissingOption: true,
      disabled: true,
      inputClassName: "mt-1",
    },
    id: {
      type: "transactionSearch",
      filterInput: [
        { invoiceId: invoice?.id },
        { typeId: { value: 8, operator: "eq" } },
      ],
    },
    description: {
      type: "text",
      disabled: true,
      inputClassName: "truncate",
    },
    amount: { type: "number-currency", allowNegativeInput: false },
  };

  const creditItemInputs = {
    className:
      "mt-5 grid min-w-[44rem] grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 border-b-[1px] pb-4",
    inputs: [
      ...creditFormik.values.items.flatMap((item, index) =>
        Object.keys(item)
          .filter(key => !/^remainingAmount_\d+$/.test(key))
          .map(key => {
            const fieldType = key?.split("_")?.[0];
            const fieldNumber = key?.match(/\d+/)?.[0];

            if (fieldType === "action") {
              return {
                actionClassName: "mt-2 flex h-[38px] items-center",
                name: "items",
                type: "removeDynamicInputs",
                deleteIndex: index,
                deleteKeys: Object.keys(item),
              };
            }

            return {
              name: key,
              ...inputConfigs[fieldType],
              ...(fieldType === "description" && {
                title: creditFormik.values[key],
              }),
              ...(fieldType === "amount" && {
                disabled: !creditFormik.values[`id_${fieldNumber}`],
                title: !creditFormik.values[`id_${fieldNumber}`]
                  ? "Select a Reference first"
                  : undefined,
              }),
            };
          }),
      ),
    ],
  };

  const creditItemInputProps = {
    formik: creditFormik,
    ...creditItemInputs,
  };

  const handleAddItem = () => {
    setCreditAllError("");
    const nextItemNumber = getNextCreditRowNumber(creditFormik.values.items);
    const selectedIds = creditFormik.values.items
      .map(item => Object.keys(item).find(key => /^id_\d+$/.test(key)))
      .map(key => creditFormik.values[key])
      .filter(Boolean);
    const defaultReferenceId = getDefaultCreditReferenceId({
      invoiceItems: invoice?.items,
      selectedIds,
    });
    const defaultItem = buildCreditRow({
      id: defaultReferenceId,
      number: nextItemNumber,
    });
    creditFormik?.setValues({
      ...creditFormik.values,
      ...defaultItem,
      items: [...creditFormik.values.items, defaultItem],
    });
  };

  const handleCreditAll = async () => {
    setCreditAllItemLoading(true);
    setCreditAllError("");

    try {
      const sourceItems = getCreditAllSourceItems(invoice?.items);
      if (!sourceItems.length) {
        throw new Error("No Sales are available to credit on this invoice.");
      }

      const creditItems = await Promise.all(
        sourceItems.map(async item => {
          const { data: { itemCreditDetails } = {} } =
            await fetchItemCreditDetails({
              variables: { id: item.id },
            });
          return { details: itemCreditDetails, id: item.id };
        }),
      );
      const creditRows = buildCreditAllRows({
        availableBalance: invoice?.invoiceTotals?.balance,
        creditItems,
      });
      if (!creditRows.length) {
        throw new Error("No remaining Sales are available to credit.");
      }

      prefillingCreditAllRef.current = true;
      await creditFormik?.setValues(
        {
          invoiceId: creditFormik.values.invoiceId,
          items: creditRows,
          typeId: creditFormik.values.typeId,
          ...Object.assign({}, ...creditRows),
        },
        false,
      );
    } catch (error) {
      setCreditAllError(error?.message || "Unable to load Credit lines.");
    } finally {
      setCreditAllItemLoading(false);
    }
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex flex-1 flex-col text-2xl font-bold">
          <div>Add Transaction</div>
          {formError && (
            <small className="text-sm font-medium text-red-600">
              {createItemCreditError?.message ||
                createItemCreditRespError ||
                creditAllError}
            </small>
          )}
        </div>
      </div>
      <div className="grid">
        <GridInputs {...creditInputProps} />
      </div>
      <div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>Credits</div>
          <div className="flex flex-wrap gap-4">
            <Button
              label="Credit All"
              disabled={creditAllItemLoading || creditItemLoading}
              loading={creditAllItemLoading}
              onClick={handleCreditAll}
            />
            <Button
              label="Add Credit"
              disabled={creditAllItemLoading || creditItemLoading}
              onClick={handleAddItem}
            />
          </div>
        </div>
        <div className="mt-1 text-sm text-gray-600">
          Select a Reference first, then enter the amount to credit.
        </div>
        <div className="w-full overflow-x-auto overscroll-x-contain">
          <div className="mt-5 grid min-w-[44rem] grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 border-b-[1px] pb-4">
            <h5 className="text-sm font-bold">Reference</h5>
            <h5 className="text-sm font-bold">Nominal</h5>
            <h5 className="text-sm font-bold">Description</h5>
            <h5 className="text-sm font-bold">Amount</h5>
            <span />
          </div>
          <GridInputs {...creditItemInputProps} />
        </div>
      </div>
      <div className="mt-4 flex flex-wrap justify-end gap-4">
        <Button
          action="default"
          label="Cancel"
          onClick={() => {
            resetAll();
            creditFormik?.resetForm?.();
          }}
        />
        <Button
          label="Add Credit Notes"
          disabled={
            creditItemLoading ||
            hasCreditErrors ||
            mutationError ||
            creditFormik.values.items.length === 0
          }
          loading={creditItemLoading}
          onClick={creditFormik?.submitForm}
        />
      </div>
    </div>
  );
};

export default AddCreditNoteBody;
